import React, { memo } from 'react';

import { Box, Typography } from '@mui/material';
import { FileClockIcon } from '@sharedCR/coreDesignSystem/icons';
import LabelCDS from '@sharedCR/coreDesignSystem/LabelCDS';
import { LabelVariant } from '@sharedCR/coreDesignSystem/LabelCDS/LabelCDS.constants';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import { BorrowersDeliveryStepTestIds } from '../../BorrowersDeliveryStep.testIds';

const cardStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  padding: '18px 20px',
  borderRadius: '10px',
  border: ({ palette }) => `1px solid ${palette.border.input}`,
  backgroundColor: ({ palette }) => palette.background.paper,
};

const headerStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  color: ({ palette }) => palette.success.main,
};

const dateStyles = {
  marginTop: '4px',
  color: ({ palette }) => palette.text.primary,
};

const subStyles = {
  color: ({ palette }) => palette.text.secondary,
};

const titleStyles = { color: ({ palette }) => palette.text.primary };

const chipWrapperStyles = { marginLeft: 'auto' };

const formatLongDate = date => (date ? dayjs(date).format('MMM D, YYYY') : null);
const formatShortDate = date => (date ? dayjs(date).format('MMM D') : null);

const getSubtitle = (ecdEstimate, isMailProviderEnabled) => {
  if (!ecdEstimate.hasEstimate) {
    return 'An estimate appears once the loan has an earliest closing date on file.';
  }

  if (ecdEstimate.anyMail) {
    const mailboxRule = `Assumes the mailbox rule: receipt presumed ${formatShortDate(
      ecdEstimate.mailReceiptDate,
    )} (3 business days) — estimated earliest closing ${formatLongDate(
      ecdEstimate.estimatedDate,
    )}.`;

    return isMailProviderEnabled
      ? `${mailboxRule} The date becomes authoritative once the mail provider confirms the send.`
      : `${mailboxRule} The authoritative date is set after you record Mark as mailed.`;
  }

  return `Sending today · all signers e-sign — estimated earliest closing ${formatLongDate(
    ecdEstimate.estimatedDate,
  )}.`;
};

const EarliestClosingImpactCard = ({ ecdEstimate, isMailProviderEnabled }) => (
  <Box data-testid={BorrowersDeliveryStepTestIds.ECD_IMPACT} sx={cardStyles}>
    <Box sx={headerStyles}>
      <FileClockIcon size={16} />
      <Typography sx={titleStyles} variant="bold14">
        Earliest closing impact
      </Typography>
      <Box sx={chipWrapperStyles}>
        <LabelCDS size="S" title="ESTIMATED" variant={LabelVariant.BLUE} />
      </Box>
    </Box>
    <Typography
      data-testid={BorrowersDeliveryStepTestIds.ECD_IMPACT_DATE}
      sx={dateStyles}
      variant="bold24"
    >
      {formatLongDate(ecdEstimate.estimatedDate) ?? '—'}
    </Typography>
    <Typography sx={subStyles} variant="regular12">
      {getSubtitle(ecdEstimate, isMailProviderEnabled)}
    </Typography>
  </Box>
);

EarliestClosingImpactCard.propTypes = {
  ecdEstimate: PropTypes.shape({
    hasEstimate: PropTypes.bool,
    anyMail: PropTypes.bool,
    estimatedDate: PropTypes.instanceOf(Date),
    mailReceiptDate: PropTypes.instanceOf(Date),
  }).isRequired,
  isMailProviderEnabled: PropTypes.bool,
};

export default memo(EarliestClosingImpactCard);
